import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft, CaretRight, ShieldCheckered,
  UsersThree, ShieldCheck, SlidersHorizontal, Database, Robot, PlugsConnected,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_SECTIONS } from "@/lib/adminSections";
import { useSmartBack } from "@/lib/useSmartBack";
import { cn } from "@/lib/utils";

/**
 * Numbered stage groups for the Administration console. Mirrors the client-
 * workspace lifecycle pattern (01–06 numbered stages, orange highlight for
 * the active stage, accordion for sub-items) so admins get the same calm
 * vertical spine as staff working inside a client workspace.
 */
const ADMIN_STAGES = [
  { num: "01", key: "access",       label: "User & Access",         icon: UsersThree,       group: "User & Access Management" },
  { num: "02", key: "security",     label: "Environment & Security", icon: ShieldCheck,     group: "Environment & Security" },
  { num: "03", key: "platform",     label: "Platform Configuration", icon: SlidersHorizontal, group: "Platform Configuration" },
  { num: "04", key: "data",         label: "Data & Intelligence",    icon: Database,        group: "Data & Intelligence" },
  { num: "05", key: "ai-governance", label: "AI Governance",         icon: Robot,           group: "AI Governance" },
  { num: "06", key: "integrations",  label: "Integrations & Health", icon: PlugsConnected,  group: "Integrations & Health" },
];

function SectionLink({ section, active, onGo }) {
  return (
    <button
      data-testid={`adminnav-section-${section.key}`}
      onClick={() => onGo(section.key)}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group relative flex w-full items-center gap-2.5 py-1.5 pl-9 pr-3 text-left text-[13px] transition-colors",
        active ? "font-semibold text-sidebar-foreground" : "text-sidebar-muted hover:text-sidebar-foreground"
      )}
    >
      {active && <span className="absolute left-[26px] top-1/2 h-4 w-[2px] -translate-y-1/2 bg-accent-ai" />}
      <section.icon
        className={cn("h-4 w-4 shrink-0", active ? "text-accent-ai" : "text-sidebar-muted/70 group-hover:text-sidebar-foreground")}
        weight={active ? "fill" : "regular"}
      />
      <span className="truncate">{section.label}</span>
    </button>
  );
}

/**
 * Context-aware navigation for the Administration console. When a user is on
 * /admin/* the global sidebar transforms into THIS: a back-to-app header,
 * an "Administration" identity chip, and the 6 numbered groups as a vertical
 * accordion whose sub-links drive `/admin/:section`.
 */
export function AdminNavPanel({ collapsed = false, onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const back = useSmartBack("/");
  const { can } = useAuth();

  const activeKey = location.pathname.split("/")[2] || "overview";
  const visibleSections = ADMIN_SECTIONS.filter((s) => can(s.perm));

  const stageOf = (sectionKey) => {
    const meta = visibleSections.find((s) => s.key === sectionKey);
    if (!meta) return -1;
    return ADMIN_STAGES.findIndex((st) => st.group === meta.group);
  };
  const activeStageIndex = stageOf(activeKey);
  const [openStage, setOpenStage] = useState(activeStageIndex);
  useEffect(() => { if (activeStageIndex >= 0) setOpenStage(activeStageIndex); }, [activeStageIndex]);

  const goSection = (key) => {
    navigate(key === "overview" ? "/admin" : `/admin/${key}`);
    onNavigate?.();
  };
  const goStage = (stage) => {
    // Jump to the first visible section in the stage.
    const firstSection = visibleSections.find((s) => s.group === stage.group);
    if (firstSection) goSection(firstSection.key);
  };
  const goBack = () => { back(); onNavigate?.(); };

  // Collapsed rail — one stage icon per row.
  if (collapsed) {
    return (
      <div className="flex h-full flex-col bg-shell-base" data-testid="admin-nav-panel">
        <button onClick={goBack} title="Back to previous page"
          data-testid="adminnav-back"
          className="flex items-center justify-center border-b border-shell-border py-3 text-shell-muted transition-colors hover:text-shell-text">
          <ArrowLeft className="h-4 w-4" weight="bold" />
        </button>
        <nav className="flex-1 space-y-1 overflow-y-auto py-2">
          {ADMIN_STAGES.map((s, i) => {
            const on = i === activeStageIndex;
            const hasVisible = visibleSections.some((sec) => sec.group === s.group);
            if (!hasVisible) return null;
            return (
              <button key={s.key} onClick={() => goStage(s)} title={`${s.num} · ${s.label}`}
                data-testid={`adminnav-stage-${s.key}`}
                className={cn(
                  "relative mx-2 flex items-center justify-center rounded-[2px] py-2.5 transition-colors",
                  on ? "bg-card text-foreground ring-1 ring-border" : "text-sidebar-muted hover:bg-sidebar-hover/70 hover:text-sidebar-foreground"
                )}>
                <s.icon className={cn("h-[18px] w-[18px]", on && "text-accent-ai")} weight={on ? "fill" : "bold"} />
              </button>
            );
          })}
        </nav>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-shell-base" data-testid="admin-nav-panel">
      {/* Back to previous page */}
      <button onClick={goBack} data-testid="adminnav-back"
        className="flex items-center gap-2 border-b border-shell-border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-shell-muted transition-colors hover:text-shell-text">
        <ArrowLeft className="h-3.5 w-3.5" weight="bold" /> Back
      </button>

      {/* Administration identity */}
      <div className="border-b border-shell-border px-3 py-3">
        <p className="mb-1.5 px-1 font-mono text-[9px] uppercase tracking-[0.2em] text-shell-muted">Administration</p>
        <button
          data-testid="adminnav-overview"
          onClick={() => goSection("overview")}
          className={cn(
            "group flex w-full items-center gap-2.5 border border-shell-border bg-shell-surface px-2.5 py-2 text-left transition-all hover:border-accent-ai/40 hover:bg-shell-surface-hover",
            activeKey === "overview" && "border-accent-ai/60 bg-shell-surface-hover"
          )}
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-shell-border bg-shell-base text-accent-ai">
            <ShieldCheckered className="h-4 w-4" weight="fill" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-shell-text">Tokuro Platform</span>
            <span className="block truncate font-mono text-[9px] uppercase tracking-wide text-shell-muted">Overview &amp; controls</span>
          </span>
        </button>
      </div>

      {/* Numbered stages accordion */}
      <nav className="flex-1 overflow-y-auto py-1" aria-label="Administration sections" data-testid="adminnav-accordion">
        {ADMIN_STAGES.map((s, i) => {
          const sections = visibleSections.filter((sec) => sec.group === s.group);
          if (sections.length === 0) return null;
          const expanded = openStage === i;
          const isCurrent = i === activeStageIndex;
          return (
            <div key={s.key} className="border-b border-shell-border/50">
              <div className={cn("flex w-full items-center transition-colors",
                isCurrent ? "text-sidebar-foreground" : "text-sidebar-muted")}>
                <button
                  onClick={() => { goStage(s); setOpenStage(i); }}
                  data-testid={`adminnav-stage-${s.key}`}
                  className="flex flex-1 items-center gap-2.5 py-2.5 pl-4 pr-1 text-left transition-colors hover:text-sidebar-foreground"
                >
                  <s.icon className={cn("h-[18px] w-[18px] shrink-0", isCurrent ? "text-accent-ai" : "")} weight={isCurrent ? "fill" : "bold"} />
                  <span className={cn("font-mono text-[10px]", isCurrent ? "text-accent-ai" : "text-shell-muted/70")}>{s.num}</span>
                  <span className="flex-1 truncate text-sm font-medium">{s.label}</span>
                </button>
                <button
                  onClick={() => setOpenStage(expanded ? -1 : i)}
                  data-testid={`adminnav-stage-toggle-${s.key}`}
                  aria-expanded={expanded}
                  aria-label={`${expanded ? "Collapse" : "Expand"} ${s.label} sub-sections`}
                  className="flex items-center px-3 py-2.5 text-shell-muted transition-colors hover:text-sidebar-foreground"
                >
                  <CaretRight className={cn("h-3.5 w-3.5 shrink-0 transition-transform", expanded && "rotate-90")} weight="bold" />
                </button>
              </div>
              {expanded && (
                <div className="pb-1.5">
                  {sections.map((sec) => (
                    <SectionLink key={sec.key} section={sec} active={activeKey === sec.key} onGo={goSection} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
}

export default AdminNavPanel;
